import React from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { ToggleGroup, ToggleGroupItem } from "components/ui/toggle-group";
import SiteFobsList from "./SiteFobsList";
import SiteKeyAuditTable from "./SiteKeyAuditTable";
import SiteKeyDetail from "./SiteKeyDetail";

type KeysView = "fobs" | "audit";

/**
 * The Fobs | Audit log switch at the top of the site's Keys tab. Links are built from siteId so
 * the control works the same wherever the site page is mounted.
 */
function KeysViewSwitch({ siteId, view }: { siteId: string; view: KeysView }) {
  const navigate = useNavigate();
  const keysPath = `/admin/sites/${siteId}/keys`;

  return (
    <ToggleGroup
      aria-label="Keys view"
      type="single"
      value={view}
      onValueChange={(next) => {
        // Radix lets a single toggle group be cleared; keep one view selected.
        if (!next || next === view) return;
        navigate(next === "audit" ? `${keysPath}/audit` : keysPath);
      }}
    >
      <ToggleGroupItem className="px-3" value="fobs">
        Fobs
      </ToggleGroupItem>
      <ToggleGroupItem className="px-3" value="audit">
        Audit log
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

function KeysSection({
  siteId,
  view,
  children,
}: {
  siteId: string;
  view: KeysView;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-4">
      <KeysViewSwitch siteId={siteId} view={view} />
      {children}
    </div>
  );
}

/**
 * The site page's Keys tab: the Fobs list (index), the site-wide Audit log (`audit`) and the
 * fob detail (`:credential/*`). Credentials are 32-hex, so the literal `audit` can't collide.
 */
export default function SiteKeys({
  siteId,
  externalSyncEnabled,
}: {
  siteId: string;
  /** A Schlage site sharing physical keys with sister sites: show the External sync status. */
  externalSyncEnabled: boolean;
}) {
  return (
    <Routes>
      <Route
        index
        element={
          <KeysSection siteId={siteId} view="fobs">
            <SiteFobsList siteId={siteId} showExternalSync={externalSyncEnabled} />
          </KeysSection>
        }
      />
      <Route
        path="audit"
        element={
          <KeysSection siteId={siteId} view="audit">
            <SiteKeyAuditTable siteId={siteId} />
          </KeysSection>
        }
      />
      <Route
        path=":credential/*"
        element={
          <SiteKeyDetail siteId={siteId} externalSyncEnabled={externalSyncEnabled} />
        }
      />
    </Routes>
  );
}
